import { ReactNode } from 'react';
import { IGeoLocation } from './geotracking';
import { IIncludeTypeAPI } from './included';
import { IComponentName } from './pages';
import { PackageType } from './tariffs';
import dayjs from 'dayjs';
import { IGetApiType } from '../REST';
import { UnitTypeValues } from '../middleware';
import { ITrPage } from '../translate';

export * from '../REST/types';
export * from './activityLog';
export * from './and';
export * from './apiMessages';
export * from './auth';
export * from './balanceLog';
export * from './billingSystem';
export * from './callDataRecords';
export * from './card';
export * from './cardSet';
export * from './cardSetManage';
export * from './cardSets';
export * from './catalogs';
export * from './companies';
export * from './company';
export * from './countries';
export * from './currencies';
export * from './dashboard';
export * from './diameterSessions';
export * from './esNotifications';
export * from './externalAPIAPI';
export * from './externalAPIDic';
export * from './externalAPIFront';
export * from './featureFlagsList';
export * from './form';
export * from './geotracking';
export * from './htmlComponents';
export * from './included';
export * from './invoice';
export * from './layout';
export * from './monthlyOrders';
export * from './myAlarm';
export * from './operatorInteractions';
export * from './operators';
export * from './order';
export * from './orders';
export * from './package';
export * from './packages';
export * from './packagesUnitsLogs';
export * from './paperTrailLogs';
export * from './pages';
export * from './purchase';
export * from './purchases';
export * from './rate';
export * from './rates';
export * from './reports';
export * from './router';
export * from './serverNotification';
export * from './subscriber';
export * from './subscribers';
export * from './table';
export * from './tariffs';
export * from './typeAny';
export * from './ui';
export * from './user';
export * from './users';
export * from './window';
export * from './zones';

export type IStringOrNull = string | null | undefined;

export type IRoutes = IRoute[];

export interface IRoute {
    name: string;
    pattern: string;
}

export type INameObject = {
    id?: string;
    name?: string;
};
export type INameObjectN = Omit<INameObject, 'id'> & {
    id?: number;
};

export type INameObjectIdN = Omit<INameObject, 'id'> & {
    id: number;
};

export type INameObjectIdNApi = INameObjectIdN | null;

export type INumberOrUndefined = number | undefined;
export type IFilterSelect = string | undefined;
export type IFilterMultipleSelect = string[];
export type IFilterSelectComposed = IFilterSelect | IFilterMultipleSelect;
export type IFilterBoolean = boolean | undefined;
export type IExportType = 'csv' | 'pdf';

export enum SelectType {
    SINGLE = 'single',
    MULTIPLE = 'multiple',
    TAGS = 'tags',
}

export type IDateMonth = [
    number, // number of months before the current month
    number, // number of months after the current month
];

export type IFilterParams = {
    buyerId?: boolean;
    cardId?: boolean;
    cardSetId?: boolean;
    commandId?: boolean;
    companyId?: boolean;
    companyTypeId?: boolean;
    dateMonth?: IDateMonth;
    dateRange?: boolean;
    defaultDateRangeType?: 'month' | 'day';
    defaultUnitTypes?: UnitTypeValues;
    entityId?: boolean;
    exportType?: IExportType;
    fileId?: boolean;
    gatewayId?: boolean;
    importType?: boolean;
    isActiveThisMonth?: boolean;
    isCardSetIdBlockClear?: boolean;
    isCompanyIdBlockClear?: boolean;
    isCompanyTypeIdBlockClear?: boolean;
    isDateRangeCleanedDefaultURL?: boolean;
    isDateRangeMaxDayToDay?: boolean;
    isDateRangeMinDayOn?: boolean;
    isDateYearsOnly?: boolean;
    isDelete?: boolean;
    isEimProvided?: boolean;
    isMaxDateRange?: boolean;
    isOperatorIdBlockClear?: boolean;
    isPackageIdBlockClear?: boolean;
    isPaymentTypeBlockClear?: boolean;
    isSessionIdBlockClear?: boolean;
    isUnitTypesBlockClear?: boolean;
    isUserIdBlockClear?: boolean;
    isZoneIdBlockClear?: boolean;
    operatorId?: boolean;
    packageId?: boolean;
    pageNumber?: boolean;
    pageSize?: boolean;
    paymentType?: boolean;
    search?: boolean;
    sellerId?: boolean;
    sessionId?: boolean;
    source?: boolean;
    status?: boolean;
    tariffId?: boolean;
    tariffPackageId?: boolean;
    unitTypes?: SelectType;
    userId?: boolean;
    withDeleted?: boolean;
    zoneId?: boolean;
};

export type IParamsList = {
    id?: string;
    // orderBy?: string;
    // orderDirection?: 'desc' | 'asc';
    assignmentScope?: 'future' | 'parent_blocked';
    boughtForMyCompany?: boolean;
    buyerId?: IFilterSelectComposed;
    byInvoicing?: boolean;
    ids?: IFilterSelectComposed;
    cardId?: IFilterSelectComposed;
    cardSetId?: IFilterSelectComposed;
    cardSetsId?: IFilterSelectComposed;
    cardsSubsCompanyId?: string;
    commandId?: IFilterSelectComposed;
    companyId?: IFilterSelectComposed;
    assignedCompanyId?: IFilterSelectComposed;
    companyInvoice?: boolean;
    companyTypeId?: IFilterSelectComposed;
    countryId?: string;
    date?: dayjs.Dayjs;
    day?: number;
    defaultUnitTypes?: UnitTypeValues;
    endDate?: string;
    entityId?: IFilterSelectComposed;
    entityType?: string;
    exportType?: IExportType;
    fileId?: IFilterSelectComposed;
    fromDate?: string;
    fromMonth?: string;
    gatewayId?: IFilterSelectComposed;
    importType?: IFilterSelectComposed;
    invoiceable?: boolean;
    invoiceDate?: string;
    isActiveThisMonth?: boolean;
    isActivityLogUsers?: boolean;
    isDelete?: boolean;
    isEimProvided?: boolean;
    mfa_token?: string;
    month?: number;
    networkId?: IFilterSelectComposed;
    operatorId?: IFilterSelectComposed;
    packageId?: IFilterSelectComposed;
    packageType?: PackageType;
    pageNumber?: number;
    pageSize?: number;
    paginate?: 'false';
    parentId?: string;
    paymentType?: IFilterSelectComposed;
    prev?: number;
    read?: string;
    routeName?: IComponentName;
    search?: string;
    sellerId?: IFilterSelectComposed;
    sessionId?: IFilterSelectComposed;
    source?: IFilterSelectComposed;
    srlNetworksCompanyId?: string;
    startDate?: string;
    status?: IFilterSelectComposed;
    stream?: string;
    tariffCountryIds?: string[];
    tariffId?: IFilterSelectComposed;
    tariffPackageId?: IFilterSelectComposed;
    tillMonth?: string;
    toDate?: string;
    transactionId?: string;
    unitType?: string;
    unitTypeIds?: IFilterSelectComposed;
    unitTypes?: IFilterSelectComposed;
    usageScheduled?: boolean;
    userId?: IFilterSelectComposed;
    withDeleted?: boolean;
    withoutMno?: boolean;
    year?: number;
    zoneId?: IFilterSelectComposed;
};

export type IDateRangeAPI = {
    start_date?: string;
    end_date?: string;
};

export type IDateRange = {
    startDate?: string | undefined;
    endDate?: string | undefined;
};

export type ISelectList = Array<ISelect>;
export type ISelectWithTotal = {
    select: ISelectList;
    total: number;
};
export type ISelectFind = ISelect | undefined;

export interface ISelect {
    id: string;
    name: string;
    value?: string;
    disabled?: boolean;
}

export interface INewSelectApi {
    [key: string]: string[];
}

export type IColumnOption = {
    xs?: number; //   <  576
    sm?: number; //  >=  576
    md?: number; //  >=  768
    lg?: number; //  >=  992
    xl?: number; //  >= 1200
    xxl?: number; // >= 1600
};

export type IStringOrNullAPI = string | undefined;

export interface IRelationshipsItemPOSTAPI {
    id: string;
    type: IIncludeTypeAPI;
    destroy?: boolean;
}

export type IChangeStatusCommands = 'unblock' | 'block' | 'activate' | 'refresh';
export type IChangeStatus = {
    command: IChangeStatusCommands;
    idList: Array<string>;
};

export type ITempTableDataSourceDefaultItem = {
    key: string;
};

export type ITempTableDataSourceAnyFieldItem = {
    [field: string]: string;
};

export type ITempTableDataSourceItem = {
    [id: string]: ReactNode;
};

export type ITempTableColumnItem = {
    title?: string;
    dataIndex?: string;
    className?: string;
    width?: string | number;
};

export type IRowSelectionCheckboxProps = {
    disabled?: boolean;
};

export type ICardWithIdSpinners = {
    children?: never;
    columnOption?: IColumnOption;
    idSpinners: IGetApiType[];
    title?: ITrPage;
};

export type IIdSpinners = {
    idSpinners?: IGetApiType[];
};

export type IGetListMapCenterAndZoom = {
    center: IGeoLocation;
    zoom: number;
};

export type IComponentV2 = {
    isV2?: boolean;
};

export type IMultiEnabled = {
    [id: string]: {
        enabled?: boolean;
    };
};

export type IGetDeployVersion = {
    apiHost?: string;
    deployTime?: string;
    deployVersion?: string;
};
